import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { PublicLayout } from '@/components/layout/PublicLayout';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { api } from '@/lib/api';
import { useAuth } from '@/lib/auth';
import { toast } from 'sonner';
import { Layers, ShieldCheck } from 'lucide-react';

export default function ResellerLoginPage() {
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [loading, setLoading] = useState(false);
    const { loginReseller, loginAdmin } = useAuth();
    const navigate = useNavigate();

    const submit = async (e) => {
        e.preventDefault();
        setLoading(true);
        try {
            const { data } = await api.post('/auth/reseller/login', { email, password });
            loginReseller(data.token, data.reseller);
            toast.success('Welcome back!');
            navigate('/reseller');
        } catch (err) {
            // Fall back to admin credentials — admins can sign in from this same form.
            try {
                const { data } = await api.post('/auth/admin/login', { email, password });
                loginAdmin(data.token, data.admin);
                toast.success('Welcome back, Admin!');
                navigate('/admin');
            } catch {
                toast.error(err.response?.data?.detail || 'Invalid credentials');
            }
        } finally { setLoading(false); }
    };

    return (
        <PublicLayout>
            <div className="min-h-[70vh] flex items-center justify-center px-4 py-10">
                <Card className="max-w-md w-full p-6 sm:p-8">
                    <div className="flex items-center gap-2 mb-1">
                        <div className="h-10 w-10 rounded-lg bg-[#1F9D8A] text-white flex items-center justify-center"><Layers className="h-5 w-5" /></div>
                        <div>
                            <div className="font-display font-semibold text-lg">Reseller Login</div>
                            <div className="text-xs text-muted-foreground">Use your allocated diamonds & coins.</div>
                        </div>
                    </div>
                    <div className="mt-4 flex items-center gap-2 text-[12px] text-muted-foreground">
                        <ShieldCheck className="h-3.5 w-3.5" /> Registered resellers only.
                    </div>
                    <form onSubmit={submit} className="mt-6 space-y-4">
                        <div className="space-y-2">
                            <Label htmlFor="email">Email</Label>
                            <Input id="email" data-testid="reseller-login-email-input" type="email" value={email} onChange={e => setEmail(e.target.value)} required />
                        </div>
                        <div className="space-y-2">
                            <Label htmlFor="password">Password</Label>
                            <Input id="password" data-testid="reseller-login-password-input" type="password" value={password} onChange={e => setPassword(e.target.value)} required />
                        </div>
                        <Button type="submit" disabled={loading} className="w-full bg-[#0E2A47] hover:bg-[#0B223A]" data-testid="reseller-login-submit-button">{loading ? 'Signing in...' : 'Sign in as Reseller'}</Button>
                    </form>
                    <div className="mt-4 text-xs text-muted-foreground text-center">Demo: reseller@shinningeyes.com / Reseller@123</div>
                    <div className="mt-2 text-center text-sm">
                        <Link to="/forgot-password?role=reseller" className="text-muted-foreground hover:text-foreground hover:underline" data-testid="reseller-forgot-password-link">Forgot password?</Link>
                    </div>
                </Card>
            </div>
        </PublicLayout>
    );
}
